/** Griffin staff admin tab on ClientDetail to manage the Client Portal:
 *  - Manage portal users (CRUD + reset password)
 *  - Bulk upload Excel (Sales / Receivables / Payables / Compliance)
 *  - Manage RFIs (create + view responses)
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UserPlus, KeyRound, Trash2, Upload, Download, Plus, MessageSquare, X, Paperclip, Mail,
} from "lucide-react";

const DATA_KINDS = [
  { key: "sales_b2b", label: "Sales B2B" },
  { key: "sales_b2c", label: "Sales B2C" },
  { key: "sales_returns", label: "Sales Return / Credit Note" },
  { key: "missing_invoices", label: "Missing Invoices" },
  { key: "gstr1_unfiled", label: "GSTR-1 Unfiled by Client" },
  { key: "suspense", label: "Suspense Account" },
  { key: "receivables", label: "Payment Receivables" },
  { key: "payables", label: "Payment Payables" },
  { key: "compliance", label: "Compliance Calendar" },
];

export default function ClientPortalTab({ clientId, canManage, isAdmin = false }) {
  return (
    <div data-testid="client-portal-tab">
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 overflow-x-auto flex-nowrap">
          <PortalSubTab value="users" label="Portal Users" />
          <PortalSubTab value="uploads" label="Data Uploads" />
          <PortalSubTab value="rfis" label="Queries / RFIs" />
          <PortalSubTab value="visibility" label="Section Visibility" />
        </TabsList>
        <TabsContent value="users" className="pt-5"><UsersSection clientId={clientId} canManage={canManage} /></TabsContent>
        <TabsContent value="uploads" className="pt-5"><UploadsSection clientId={clientId} canManage={canManage} /></TabsContent>
        <TabsContent value="rfis" className="pt-5"><RfisSection clientId={clientId} canManage={canManage} /></TabsContent>
        <TabsContent value="visibility" className="pt-5"><VisibilitySection clientId={clientId} canManage={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

const PortalSubTab = ({ value, label }) => (
  <TabsTrigger value={value} className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid={`cp-staff-subtab-${value}`}>
    {label}
  </TabsTrigger>
);

// ============== USERS ==============
function UsersSection({ clientId, canManage }) {
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/portal-users`);
      setUsers(r.data || []);
    } catch (e) {
      toast.error("Could not load portal users");
    }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/portal-users`);
        if (active) setUsers(r.data || []);
      } catch (e) {
        if (active) toast.error("Could not load portal users");
      }
    })();
    return () => { active = false; };
  }, [clientId]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete portal user "${u.email}"?`)) return;
    try {
      await api.delete(`/client-portal-users/${u.id}`);
      toast.success("Portal user deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/client-portal-users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const resendInvitation = async (u) => {
    const msg = u.pending_activation
      ? `Resend the activation invitation to ${u.email}?`
      : `Send a fresh setup link to ${u.email}? Their current password will stop working until they activate.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/client-portal-users/${u.id}/resend-invitation`);
      toast.success(`Invitation sent to ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invitation");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Portal Users ({users.length})</h3>
          <p className="text-xs text-slate-500 mt-0.5">Company Admins (Layer 1) and Employees (Layer 2) who can sign in at <span className="font-mono">/client-portal/login</span>.</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)} size="sm" className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="btn-create-portal-user">
            <UserPlus size={14} className="mr-2" /> New portal user
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        {users.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No portal users yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Last login</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cp-user-row-${u.id}`}>
                  <Td className="font-semibold">{u.name || "—"}</Td>
                  <Td className="font-mono">{u.email}</Td>
                  <Td>
                    <span className={`px-2 py-0.5 text-[10px] rounded-sm uppercase tracking-wide font-semibold ${u.role === "company_admin" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>
                      {u.role === "company_admin" ? "Company Admin" : "Employee"}
                    </span>
                  </Td>
                  <Td>
                    <button onClick={() => canManage && toggleActive(u)} disabled={!canManage} className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                      {u.active ? "Active" : "Inactive"}
                    </button>
                  </Td>
                  <Td className="text-slate-500">
                    {u.pending_activation ? (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold bg-amber-100 text-amber-800" title="User has not yet activated their account">
                        Pending invite
                      </span>
                    ) : (
                      u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"
                    )}
                  </Td>
                  <Td>
                    {canManage && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => resendInvitation(u)} className="p-1 hover:bg-slate-100 rounded-sm" title={u.pending_activation ? "Resend invitation email" : "Send a fresh invitation (rotates the password)"} data-testid={`btn-resend-invite-${u.id}`}>
                          <Mail size={13} />
                        </button>
                        <button onClick={() => setResetUser(u)} className="p-1 hover:bg-slate-100 rounded-sm" title="Set manual password" data-testid={`btn-reset-pw-${u.id}`}>
                          <KeyRound size={13} />
                        </button>
                        <button onClick={() => deleteUser(u)} className="p-1 hover:bg-red-50 text-red-600 rounded-sm" title="Delete" data-testid={`btn-delete-cp-user-${u.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CreatePortalUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clientId={clientId}
        onCreated={load}
      />
      <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />
    </div>
  );
}

function CreatePortalUserDialog({ open, onOpenChange, clientId, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("company_admin");
  // v3.9: default to the invitation flow — we ask if staff WANTS to set an
  // initial password manually (legacy). Otherwise the user gets an emailed
  // setup link.
  const [setManualPw, setSetManualPw] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setRole("company_admin");
    setSetManualPw(false); setPassword("");
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      return toast.error("Name and email are required");
    }
    if (setManualPw && password.length < 8) {
      return toast.error("Manual password must be at least 8 characters");
    }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), email: email.trim().toLowerCase(), role };
      if (setManualPw) payload.initial_password = password;
      await api.post(`/clients/${clientId}/portal-users`, payload);
      toast.success(setManualPw
        ? `Portal user created with the password you set.`
        : `Invitation email sent to ${email.trim().toLowerCase()}. The link is valid for 7 days.`);
      reset(); onOpenChange(false); onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-create-portal-user">
        <DialogHeader><DialogTitle>New Portal User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cp-new-user-name" /></div>
          <div className="space-y-1"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cp-new-user-email" /></div>
          <div className="space-y-1">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="cp-new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company_admin">Company Admin (Layer 1 — full access)</SelectItem>
                <SelectItem value="employee">Employee (Layer 2 — queries only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!setManualPw ? (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600 space-y-2">
              <div className="font-semibold text-[#0a2540]">📧 An invitation email will be sent</div>
              <p>
                The user gets a secure setup link (valid 7 days) to choose their own password.
                You never need to share or store a temporary password.
              </p>
              <button
                type="button"
                onClick={() => setSetManualPw(true)}
                className="text-[10px] text-slate-500 underline hover:text-[#0a2540]"
                data-testid="cp-new-user-toggle-manual"
              >
                Or set a manual password instead
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <Label>Manual password (≥8 chars)</Label>
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Generate or pick one"
                data-testid="cp-new-user-password"
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500">User must change password on first login.</p>
                <button
                  type="button"
                  onClick={() => { setSetManualPw(false); setPassword(""); }}
                  className="text-[10px] text-slate-500 underline hover:text-[#0a2540]"
                  data-testid="cp-new-user-toggle-invite"
                >
                  Send invitation email instead
                </button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-new-user-submit">
            {setManualPw ? "Create" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const submit = async () => {
    if (pw.length < 8) return toast.error("≥8 chars");
    try {
      await api.post(`/client-portal-users/${user.id}/reset-password`, { new_password: pw });
      toast.success("Password reset. Share new credentials with the user.");
      setPw(""); onClose();
    } catch (e) {
      toast.error("Reset failed");
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="dialog-reset-portal-pw">
        <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
        {user && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">For <span className="font-mono">{user.email}</span></div>
            <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" data-testid="cp-reset-pw-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-reset-pw-submit">Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== UPLOADS ==============
function UploadsSection({ clientId, canManage }) {
  const [kind, setKind] = useState("sales");
  const [rows, setRows] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/portal-data/${kind}`);
      setRows(r.data || []);
    } catch (e) {
      setRows([]);
    }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/portal-data/${kind}`);
        if (active) setRows(r.data || []);
      } catch (e) {
        if (active) setRows([]);
      }
    })();
    return () => { active = false; };
  }, [kind, clientId]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/portal-data/template/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}_template.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Template download failed");
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/clients/${clientId}/portal-data/${kind}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      toast.success(`Imported ${r.data.inserted} row${r.data.inserted === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    await api.delete(`/clients/${clientId}/portal-data/${kind}/${id}`);
    load();
  };

  const wipeAll = async () => {
    if (!window.confirm(`Delete ALL ${kind} rows for this client? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/clients/${clientId}/portal-data/${kind}`);
      toast.success(`Wiped ${r.data.deleted} rows`);
      load();
    } catch (e) {
      toast.error("Wipe failed");
    }
  };

  const exportSection = async (format) => {
    try {
      const r = await api.get(
        `/clients/${clientId}/portal-data/${kind}/export?format=${format}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m ? m[1] : `${kind}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Bulk Data Uploads</h3>
          <p className="text-xs text-slate-500 mt-0.5">Upload Excel files. Download the template first for correct column structure.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-40" data-testid="cp-upload-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATA_KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="cp-download-template">
            <Download size={14} className="mr-2" /> Template
          </Button>
          {rows.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => exportSection("xlsx")} data-testid="cp-staff-export-xlsx">
                <Download size={14} className="mr-2" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportSection("pdf")} data-testid="cp-staff-export-pdf">
                <Download size={14} className="mr-2" /> PDF
              </Button>
            </>
          )}
          {canManage && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" data-testid={`cp-upload-input-${kind}`} />
              <Button onClick={() => fileRef.current?.click()} size="sm" className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-upload-btn">
                <Upload size={14} className="mr-2" /> Upload Excel
              </Button>
              {rows.length > 0 && (
                <Button variant="ghost" size="sm" onClick={wipeAll} className="text-red-600 hover:bg-red-50" data-testid="cp-wipe-btn">
                  Wipe all
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {uploadResult && uploadResult.errors?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm mb-4">
          <div className="text-xs font-semibold mb-1">Last upload had {uploadResult.errors.length} error{uploadResult.errors.length === 1 ? "" : "s"} (other rows imported):</div>
          <ul className="text-[11px] space-y-0.5 max-h-40 overflow-y-auto">
            {uploadResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
          </ul>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid={`cp-staff-data-table-${kind}`}>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No {kind} rows yet. Upload an Excel to begin.</div>
        ) : (
          <DataTable kind={kind} clientId={clientId} rows={rows} canManage={canManage} onDelete={deleteRow} onReload={load} />
        )}
      </div>
    </div>
  );
}

function DataTable({ kind, clientId, rows, canManage, onDelete, onReload }) {
  const [openRowId, setOpenRowId] = useState(null);
  // Show ALL template columns for parity with the source Excel template.
  const COL_MAP = {
    sales_b2b: ["invoice_date","invoice_number","customer_billing_name","customer_billing_gstin","state","place_of_supply","hsn_sac","total_taxable_value","cgst_rate","cgst_amount","sgst_rate","sgst_amount","igst_rate","igst_amount","cess_rate","cess_amount","total_invoice_value"],
    sales_b2c: ["invoice_date","invoice_number","customer_billing_name","customer_billing_gstin","state","place_of_supply","hsn_sac","total_taxable_value","cgst_rate","cgst_amount","sgst_rate","sgst_amount","igst_rate","igst_amount","cess_rate","cess_amount","total_invoice_value"],
    sales_returns: ["invoice_date","credit_note_number","customer_billing_name","customer_billing_gstin","state","place_of_supply","hsn_sac","total_taxable_value","cgst_rate","cgst_amount","sgst_rate","sgst_amount","igst_rate","igst_amount","cess_rate","cess_amount","total_credit_note_value"],
    missing_invoices: ["gstin_of_supplier","trade_legal_name","invoice_number","invoice_type","invoice_date","invoice_value","place_of_supply","supply_attract_reverse_charge","taxable_value","integrated_tax","central_tax","state_ut_tax","cess","gstr1_iff_gstr5_period","gstr1_iff_gstr5_filing_date","itc_availability"],
    gstr1_unfiled: ["month","gstin_of_supplier","trade_legal_name","invoice_number","invoice_date","invoice_value","taxable_value","integrated_tax","central_tax","state_ut_tax"],
    suspense: ["entry_date","reference_no","description","debit_amount","credit_amount","status","resolved_date","notes"],
    receivables: ["invoice_no","customer_name","invoice_date","due_date","amount","paid_amount","status","notes"],
    payables: ["bill_no","vendor_name","bill_date","due_date","amount","paid_amount","status","notes"],
    compliance: ["title","category","due_date","status","description"],
  };
  const cols = COL_MAP[kind] || [];
  return (
    <>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            {cols.map((c) => <th key={c} className="px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}
            <th className="px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 whitespace-nowrap">Comments / Files</th>
            {canManage && <th className="px-3 py-2.5 sticky right-0 bg-slate-50"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((r) => {
            const cmts = (r.comments || []).length;
            const atts = (r.attachments || []).length;
            return (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                {cols.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{r[c] == null || r[c] === "" ? "—" : String(r[c])}</td>)}
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={() => setOpenRowId(r.id)}
                    className="text-[10px] flex items-center gap-2 hover:bg-slate-100 px-2 py-1 rounded-sm transition-colors"
                    data-testid={`staff-row-comments-${r.id}`}
                  >
                    <span className={`flex items-center gap-1 ${cmts > 0 ? "text-[#0a2540] font-semibold" : "text-slate-400"}`}>
                      <MessageSquare size={11} /> {cmts}
                    </span>
                    <span className={`flex items-center gap-1 ${atts > 0 ? "text-[#0a2540] font-semibold" : "text-slate-400"}`}>
                      <Paperclip size={11} /> {atts}
                    </span>
                  </button>
                </td>
                {canManage && (
                  <td className="px-3 py-2 text-right sticky right-0 bg-white">
                    <button onClick={() => onDelete(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={12} /></button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <StaffRowDetailsDialog
        rowId={openRowId}
        rows={rows}
        clientId={clientId}
        kind={kind}
        canManage={canManage}
        onClose={() => setOpenRowId(null)}
        onReload={onReload}
      />
    </>
  );
}

function StaffRowDetailsDialog({ rowId, rows, clientId, kind, canManage, onClose, onReload }) {
  const row = rowId ? rows.find((r) => r.id === rowId) : null;
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!row) return null;

  const apiBase = `/clients/${clientId}/portal-data/${kind}`;
  const downloadUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/portal-files/${fid}/download`;
  const fmtDT = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const submitComment = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`${apiBase}/rows/${row.id}/comments`, { text: text.trim() });
      setText("");
      onReload?.();
    } catch (err) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/clients/${clientId}/portal-files/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`${apiBase}/rows/${row.id}/attachments`, { attachment_ids: [r.data.id] });
      toast.success(`Attached ${f.name}`);
      onReload?.();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteSub = async (subkind, item_id) => {
    try {
      await api.delete(`${apiBase}/rows/${row.id}/${subkind}/${item_id}`);
      onReload?.();
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="staff-row-details-dialog">
        <DialogHeader><DialogTitle>Row notes & files</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Comments thread */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Comments ({(row.comments || []).length})</div>
            {(row.comments || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">No comments yet.</div>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {row.comments.map((c) => {
                  const isStaff = c.author?.kind === "staff";
                  return (
                    <li
                      key={c.id}
                      className={`border p-2 rounded-sm ${isStaff ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}
                      data-testid={`staff-comment-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] text-slate-500">
                          <span className="font-semibold text-slate-700">{c.author?.name || "—"}</span>
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded-sm text-[9px] tracking-wide uppercase font-semibold ${isStaff ? "bg-blue-200 text-blue-900" : "bg-emerald-200 text-emerald-900"}`}>
                            {isStaff ? "Griffin" : "Client"}
                          </span>
                          <span className="ml-1.5">· {fmtDT(c.created_at)}</span>
                        </div>
                        {canManage && (
                          <button onClick={() => deleteSub("comments", c.id)} className="text-slate-300 hover:text-red-500" title="Delete">
                            <X size={10} />
                          </button>
                        )}
                      </div>
                      <div className="text-xs mt-1 whitespace-pre-wrap">{c.text}</div>
                    </li>
                  );
                })}
              </ul>
            )}
            {canManage && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Reply as Griffin…"
                  data-testid="staff-row-new-comment"
                />
                <Button
                  size="sm"
                  onClick={submitComment}
                  disabled={submitting || !text.trim()}
                  className="bg-[#0a2540] hover:bg-[#0a2540]/90"
                  data-testid="staff-row-new-comment-submit"
                >
                  Send
                </Button>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Attachments ({(row.attachments || []).length})</div>
            {(row.attachments || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">No attachments.</div>
            ) : (
              <ul className="space-y-1">
                {row.attachments.map((a) => (
                  <li key={a.id} className="text-xs flex items-center justify-between bg-slate-50 px-2 py-1.5 rounded-sm" data-testid={`staff-attach-${a.id}`}>
                    <a href={downloadUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0a2540] hover:underline truncate">
                      <Paperclip size={11} /> {a.original_filename}
                    </a>
                    {canManage && (
                      <button onClick={() => deleteSub("attachments", a.id)} className="text-slate-300 hover:text-red-500 ml-2">
                        <X size={10} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canManage && (
              <div className="mt-2">
                <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" data-testid="staff-row-attach-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="staff-row-attach-btn">
                  <Upload size={11} className="mr-1.5" /> Add attachment
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============== RFIs ==============
function RfisSection({ clientId, canManage }) {
  const [rfis, setRfis] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeRfi, setActiveRfi] = useState(null);

  const load = async () => {
    try {
      const [r, u] = await Promise.all([
        api.get(`/clients/${clientId}/rfis`),
        api.get(`/clients/${clientId}/portal-users`).catch(() => ({ data: [] })),
      ]);
      setRfis(r.data || []);
      setUsers(u.data || []);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [r, u] = await Promise.all([
          api.get(`/clients/${clientId}/rfis`),
          api.get(`/clients/${clientId}/portal-users`).catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        setRfis(r.data || []);
        setUsers(u.data || []);
      } catch (e) { /* silent */ }
    })();
    return () => { active = false; };
  }, [clientId]);

  const deleteRfi = async (rfi) => {
    if (!window.confirm(`Delete RFI "${rfi.title}"? All responses will be lost.`)) return;
    try {
      await api.delete(`/clients/${clientId}/rfis/${rfi.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const closeRfi = async (rfi) => {
    try {
      await api.patch(`/clients/${clientId}/rfis/${rfi.id}`, { status: "Closed" });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Queries / RFIs ({rfis.length})</h3>
          <p className="text-xs text-slate-500 mt-0.5">Request information from the client. They respond inside the portal.</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)} size="sm" className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="btn-create-rfi">
            <Plus size={14} className="mr-2" /> New query
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        {rfis.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No queries yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rfis.map((r) => (
              <li key={r.id} className="p-4 hover:bg-slate-50" data-testid={`cp-staff-rfi-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => setActiveRfi(r)} className="text-left min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[9px] tracking-wide uppercase font-semibold px-1.5 py-0.5 rounded-sm ${r.status === "Closed" ? "bg-slate-100" : r.status === "Responded" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{r.status}</span>
                      <span className={`text-[9px] tracking-wide uppercase font-semibold px-1.5 py-0.5 rounded-sm ${r.priority === "High" ? "bg-red-100 text-red-800" : "bg-slate-100"}`}>{r.priority}</span>
                      {r.response_count > 0 && (
                        <span className="text-[10px] text-slate-500 flex items-center gap-1"><MessageSquare size={10} /> {r.response_count}</span>
                      )}
                    </div>
                    <div className="text-sm font-semibold truncate">{r.title}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Created {new Date(r.created_at).toLocaleDateString()} · {r.assigned_to_user_id ? `Assigned to ${users.find((u) => u.id === r.assigned_to_user_id)?.name || "user"}` : "Any user"}
                    </div>
                  </button>
                  {canManage && (
                    <div className="flex items-center gap-1">
                      {r.status !== "Closed" && (
                        <Button size="sm" variant="outline" onClick={() => closeRfi(r)} data-testid={`btn-close-rfi-${r.id}`}>Close</Button>
                      )}
                      <button onClick={() => deleteRfi(r)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`btn-delete-rfi-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateRfiDialog open={createOpen} onOpenChange={setCreateOpen} clientId={clientId} users={users} onCreated={load} />
      <ViewRfiDialog rfi={activeRfi} clientId={clientId} onClose={() => setActiveRfi(null)} onChanged={load} />
    </div>
  );
}

function CreateRfiDialog({ open, onOpenChange, clientId, users, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState("__any");
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setTitle(""); setDescription(""); setPriority("Medium"); setDueDate(""); setAssigneeId("__any"); setAttachments([]); };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/clients/${clientId}/portal-files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, r.data]);
    } catch {
      toast.error("Upload failed");
    } finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setSubmitting(true);
    try {
      await api.post(`/clients/${clientId}/rfis`, {
        title: title.trim(), description, priority,
        due_date: dueDate || null,
        assigned_to_user_id: assigneeId === "__any" ? null : assigneeId,
        attachment_ids: attachments.map((a) => a.id),
      });
      toast.success("Query created — client will see it in their portal");
      reset(); onOpenChange(false); onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-create-rfi">
        <DialogHeader><DialogTitle>New Query for Client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded-sm px-3 py-2">
            📧 An email notification is sent to the client portal user{ /* assigned or all admins */ } when the query is created.
          </div>
          <div className="space-y-1"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rfi-title" /></div>
          <div className="space-y-1"><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[80px]" data-testid="rfi-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="rfi-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="rfi-due" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Assign to (optional)</Label>
            <Select value={assigneeId} onValueChange={setAssigneeId}>
              <SelectTrigger data-testid="rfi-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any">Any portal user</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Attachments</Label>
            <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="rfi-attach-btn"><Upload size={12} className="mr-2" /> Add file</Button>
            <ul className="space-y-1 mt-2">
              {attachments.map((a) => (
                <li key={a.id} className="text-xs bg-slate-50 px-2 py-1 rounded-sm flex items-center justify-between">
                  <span>{a.original_filename}</span>
                  <button onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))} className="text-slate-400 hover:text-red-600"><X size={10} /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="rfi-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewRfiDialog({ rfi, clientId, onClose, onChanged }) {
  const [responses, setResponses] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    if (!rfi) return undefined;
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/rfis/${rfi.id}/responses`);
        if (active) setResponses(r.data || []);
      } catch {
        if (active) setResponses([]);
      }
    })();
    return () => { active = false; };
  }, [rfi, clientId, reloadKey]);

  const downloadUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/portal-files/${fid}/download`;
  if (!rfi) return null;

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/clients/${clientId}/portal-files/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, r.data]);
    } catch {
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!text.trim() && attachments.length === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/clients/${clientId}/rfis/${rfi.id}/respond`, {
        response_text: text.trim(),
        attachment_ids: attachments.map((a) => a.id),
      });
      setText("");
      setAttachments([]);
      setReloadKey((k) => k + 1);
      onChanged?.();
      toast.success("Reply posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!rfi} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dialog-view-rfi">
        <DialogHeader><DialogTitle>{rfi.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {rfi.description && <div className="text-slate-700 whitespace-pre-wrap">{rfi.description}</div>}
          <div className="text-xs tracking-[0.18em] uppercase text-slate-500 pt-3 border-t">Thread ({responses.length})</div>
          {responses.length === 0 ? (
            <div className="text-slate-400 italic text-sm">No replies yet.</div>
          ) : (
            <ul className="space-y-3">
              {responses.map((r) => {
                const isStaff = r.responder_role === "griffin_staff";
                return (
                  <li key={r.id} className={`border p-3 rounded-sm ${isStaff ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
                    <div className="text-[11px] text-slate-500 mb-1">
                      <span className="font-semibold text-slate-700">{r.responder_name}</span>
                      <span className="ml-1.5 text-[9px] tracking-wide uppercase px-1.5 py-0.5 rounded-sm bg-white border">
                        {isStaff ? "Griffin" : "Client"}
                      </span>
                      <span className="ml-1.5">· {new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{r.response_text || <span className="italic text-slate-400">(no text)</span>}</div>
                    {r.attachment_ids?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs">
                        {r.attachment_ids.map((fid) => (
                          <li key={fid}><a href={downloadUrl(fid)} target="_blank" rel="noreferrer" className="text-[#0a2540] hover:underline">Download attachment</a></li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {rfi.status !== "Closed" && (
            <div className="border-t pt-3" data-testid="staff-reply-form">
              <div className="text-xs tracking-[0.18em] uppercase text-slate-500 mb-2">Reply to thread</div>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your reply…" className="min-h-[80px]" data-testid="staff-reply-text" />
              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((a) => (
                    <li key={a.id} className="text-xs flex items-center justify-between bg-slate-50 px-2 py-1 rounded-sm">
                      <span className="flex items-center gap-2"><Paperclip size={11} /> {a.original_filename}</span>
                      <button onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))} className="text-slate-400 hover:text-red-600"><X size={10} /></button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between mt-2">
                <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  <Upload size={12} className="mr-2" /> Attach file
                </Button>
                <Button onClick={submit} disabled={submitting} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="staff-reply-submit">
                  Post reply
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Th = ({ children }) => <th className="px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;

// ============== VISIBILITY (Admin per-client, per-role section toggles) ==============
function VisibilitySection({ clientId, canManage }) {
  const [perm, setPerm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/section-permissions`);
        if (active) setPerm(r.data);
      } catch (e) {
        if (active) toast.error("Could not load permissions");
      }
    })();
    return () => { active = false; };
  }, [clientId]);

  const toggle = (role, key) => {
    setPerm((p) => {
      const cur = new Set(p.by_role?.[role] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...p, by_role: { ...p.by_role, [role]: [...cur] } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/section-permissions`, {
        by_role: perm.by_role,
      });
      toast.success("Section visibility saved");
      const r = await api.get(`/clients/${clientId}/section-permissions`);
      setPerm(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!perm) return <div className="text-xs text-slate-500">Loading…</div>;

  return (
    <div data-testid="cp-visibility-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Section Visibility</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Choose which sections each portal role can see. Hidden sections completely disappear from the client&apos;s sidebar AND return 404 on the API.
          </p>
          {perm.is_default && (
            <p className="text-[10px] text-amber-700 bg-amber-50 inline-block px-2 py-0.5 mt-2 rounded-sm">
              Using defaults — save to lock the settings for this client.
            </p>
          )}
        </div>
        {canManage && (
          <Button onClick={save} disabled={saving} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-visibility-save">
            Save permissions
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 text-[10px] tracking-wide uppercase text-slate-500">Section</th>
              <th className="px-4 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 text-center w-44">Company Admin</th>
              <th className="px-4 py-2.5 text-[10px] tracking-wide uppercase text-slate-500 text-center w-44">Employee</th>
            </tr>
          </thead>
          <tbody>
            {perm.all_keys.map((k) => {
              const adminSet = new Set(perm.by_role?.company_admin || []);
              const empSet = new Set(perm.by_role?.employee || []);
              return (
                <tr key={k} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5">{perm.labels?.[k] || k}</td>
                  <td className="px-4 py-2.5 text-center">
                    <Checkbox
                      checked={adminSet.has(k)}
                      onCheckedChange={() => canManage && toggle("company_admin", k)}
                      disabled={!canManage}
                      data-testid={`cp-visibility-company_admin-${k}`}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <Checkbox
                      checked={empSet.has(k)}
                      onCheckedChange={() => canManage && toggle("employee", k)}
                      disabled={!canManage}
                      data-testid={`cp-visibility-employee-${k}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
